import { z } from 'zod';
import { PLAYER_PASSWORD_MINIMUM } from './player-auth.ts';

/**
 * A SECOND FACTOR A PLAYER CHOOSES, AND A STEP-UP THEY DO NOT.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE DECISION THESE SHAPES ENCODE
 *
 * `player-auth.ts` says, and goes on saying, that PLAYER SIGN-IN VERIFIES NO
 * SECOND FACTOR. That is not a gap this file closes; it is a decision this file
 * depends on. Players are the public: a mandatory authenticator at the door
 * trades a large amount of abandonment for protection at the wrong moment. The
 * moment that matters is not somebody reading a balance, it is somebody SENDING
 * that balance to an account the player has never seen.
 *
 * So there are two halves and they are deliberately asymmetric:
 *
 *   ENROLMENT IS OPTIONAL. A player may enrol an authenticator, and most will
 *   not. Nothing about signing in changes either way.
 *
 *   THE STEP-UP IS NOT. Every player-raised withdrawal carries one. A player
 *   WITH a factor presents a code from it; a player WITHOUT one re-enters their
 *   password, and that path is additionally subject to a velocity limit —
 *   because a password is exactly what a credential-stuffing run already has.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THE STEP-UP IS NOT A SEPARATE "VERIFY" CALL
 *
 * The obvious shape is POST /verify-step-up, which returns a token the
 * withdrawal then presents. It is wrong, and the reason is the failure being
 * prevented: a hijacked session raising a withdrawal to an attacker's account.
 * A verification whose success is remembered — in a session claim, in a
 * short-lived token, in a row saying "this player is stepped up until 09:41" —
 * is a verification an attacker holding the session can spend on a REQUEST THE
 * PLAYER DID NOT MAKE. The proof has to be bound to the movement of money, not
 * to the session, so the credential travels ON the withdrawal request and is
 * verified inside the transaction that creates it.
 *
 * The same reasoning is why the `mfa` claim on a player session is minted false
 * and stays false. It is not the step-up's input and must never become one.
 */

/* ── The factor ────────────────────────────────────────────────────────────── */

/**
 * How many recovery codes are issued with a factor.
 *
 * The same ten the operator console issues. Stated here rather than left to
 * `generateRecoveryCodes`' default because the SCREEN prints the number before
 * the codes exist ("you will be given ten codes"), and a screen quoting a
 * different number from the one the server generates is the smallest possible
 * lie a security screen can tell.
 */
export const PLAYER_RECOVERY_CODE_COUNT = 10;

/**
 * A code from an authenticator app: six digits, and nothing else.
 *
 * Separators and spaces are stripped by the verifier before this ever runs —
 * `verifyTotp` in @dayon/auth does `token.replace(/\D/g, '')` — so this refuses
 * only what could never be a code however it is cleaned up.
 */
export const playerTotpCode = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'A code from your authenticator app is 6 digits.');

/**
 * A recovery code, as it was shown: two groups of five from a 32-character
 * alphabet with I, O, 0 and 1 left out, joined by a hyphen.
 *
 * Accepted case-insensitively and with the hyphen optional, because these are
 * typed off paper by somebody who has just lost their phone. `hashRecoveryCode`
 * upper-cases and trims before digesting, so the normalisation is the verifier's
 * and this only has to refuse what could never be one.
 */
export const playerRecoveryCode = z
  .string()
  .trim()
  .regex(
    /^[A-HJ-NP-Za-hj-np-z2-9]{5}-?[A-HJ-NP-Za-hj-np-z2-9]{5}$/,
    'A recovery code looks like ABCDE-FGHJK.',
  );

/**
 * EITHER of the two things an enrolled player can present.
 *
 * One field rather than two, and the SERVER decides which it is, because the
 * alternative — a `method` the caller names — is a field an attacker sets to
 * whichever branch they can satisfy. There is nothing to gain by letting a
 * request choose its own verifier.
 */
export const playerSecondFactorCode = z.union([playerTotpCode, playerRecoveryCode], {
  error: 'Enter the 6-digit code from your authenticator app, or one recovery code.',
});

/* ── Enrolling ─────────────────────────────────────────────────────────────── */

/**
 * What starting an enrolment returns.
 *
 * THIS IS THE ONE MOMENT A SECRET IS SHOWN, to the person enrolling it, on
 * their own screen — which is the only safe way an authenticator secret ever
 * moves. Nobody mails it and nobody reads it out.
 *
 * THREE WAYS IN, AND ALL THREE CARRY ONE SECRET. There was no QR here once, on
 * the grounds that drawing one means a barcode encoder and this platform adds
 * no dependency it does not need. The second half of that is still true and the
 * first half stopped being: the API has its own encoder — `services/api/src/
 * qr.ts`, written out for the affiliate portal — so the symbol costs an import
 * and no package. Each way in exists for a device the others do not serve:
 *
 *   `uri`    tappable, and on a PHONE it opens the authenticator app directly,
 *            which beats photographing the screen you are holding.
 *   `qr`     scannable, and it is the DESKTOP case — the authenticator is on a
 *            phone that cannot tap a link rendered on a laptop.
 *   `secret` typable, for anybody whose app or camera will not cooperate.
 *
 * THE QR ENCODES `uri` AND NOTHING ELSE. The route builds one string and both
 * fields come from it, because a code encoding a different secret from the one
 * printed beneath it enrols an authenticator whose codes this platform refuses
 * — discovered at a withdrawal, on a factor that cannot be switched off without
 * a code from it. The integration test decodes the symbol and compares.
 */
export const startPlayerTwoFactorResult = z.object({
  /** otpauth://totp/… — the payload, tappable on a phone. */
  uri: z.string(),
  /**
   * The same `uri` as a scannable symbol, or NULL when it could not be drawn.
   *
   * Null is a real answer and not an error: the encoder tops out at 213 bytes,
   * and a long brand name with a long address can exceed it. The enrolment
   * still works by link and by hand, so the screen says the code is unavailable
   * rather than the enrolment being refused.
   */
  qr: z
    .object({
      /** A standalone SVG document. Black on white, no font, no script. */
      svg: z.string(),
      /** Modules across the symbol, excluding the quiet zone. */
      modules: z.number().int().positive(),
      version: z.number().int().positive(),
      /** Level M — 15% of the symbol may be lost and still read. */
      errorCorrection: z.literal('M'),
      quietZone: z.number().int().nonnegative(),
      printMillimetres: z.number().int().positive(),
    })
    .nullable(),
  /** The same secret in base32, for typing in by hand. */
  secret: z.string(),
  /**
   * The name the authenticator app will file this under, RETURNED rather than
   * assumed by the screen.
   *
   * A screen that printed its own guess would be making a claim about somebody
   * else's app: this is the brand the player signed up with, and the server is
   * the only thing that knows which brand the host resolved to. It is display
   * metadata inside the otpauth URI and takes no part in generating a code —
   * `verifyTotp` derives from the secret, the algorithm, the digit count and
   * the period, and from nothing else.
   */
  issuer: z.string(),
  /** What the app will ask for. Printed, so the screen states the real terms. */
  digits: z.number().int().positive(),
  periodSeconds: z.number().int().positive(),
  /** How many recovery codes confirming will produce. */
  recoveryCodeCount: z.number().int().positive(),
});
export type StartPlayerTwoFactorResult = z.infer<typeof startPlayerTwoFactorResult>;

/**
 * Confirming an enrolment — A CODE **AND** THE PASSWORD.
 *
 * The password is not ceremony, and leaving it out is the hole this whole
 * feature would otherwise open. Consider the attacker the step-up exists to
 * stop: they hold a hijacked session on an account with NO factor. If enrolling
 * needed only the session, they enrol THEIR OWN authenticator, and from that
 * moment the withdrawal step-up is a control they satisfy and the real player
 * cannot. The step-up would protect the attacker.
 *
 * So the state change that creates a factor is gated on something the session
 * alone does not carry, exactly as switching one off is. It is the same
 * argument in both directions and it is why these two routes are written
 * together.
 */
export const confirmPlayerTwoFactorRequest = z.object({
  code: playerTotpCode,
  password: z
    .string()
    .min(1, 'Enter your account password to confirm.')
    .max(1024),
});
export type ConfirmPlayerTwoFactorRequest = z.infer<typeof confirmPlayerTwoFactorRequest>;

/**
 * What confirming returns — INCLUDING THE CODES, ONCE.
 *
 * The database holds only their SHA-256 digests, so nothing anywhere can show
 * them again: not this screen on a reload, not support, not an engineer with a
 * psql session. The screen has to say so, because a player who closes the tab
 * has lost them and the only way back is switching the factor off with a code
 * from the app and enrolling again.
 */
export const confirmPlayerTwoFactorResult = z.object({
  status: z.literal('ENROLLED'),
  enrolledAt: z.iso.datetime(),
  /** Shown once, here, and never again. */
  recoveryCodes: z.array(z.string()),
  /** Said plainly by the screen: from now on a withdrawal needs one of these. */
  withdrawalStepUp: z.literal('CODE_REQUIRED'),
});
export type ConfirmPlayerTwoFactorResult = z.infer<typeof confirmPlayerTwoFactorResult>;

/**
 * Switching a factor OFF, which needs as much care as switching it on.
 *
 * "Turn off 2FA" is precisely what an attacker holding a hijacked session
 * wants: it is the one action that converts a session they already have into
 * permanent, unguarded access to the money. So it requires a code from the
 * factor being removed, or one recovery code — the same proof the step-up
 * asks for, because it is the same question.
 *
 * THE ACCOUNT PASSWORD IS NOT ACCEPTED HERE, and that asymmetry with enrolment
 * is deliberate. A password is the credential most likely to have been stolen;
 * accepting it to remove the control that exists BECAUSE passwords get stolen
 * would make the control removable by exactly the attacker it defends against.
 */
export const disenrolPlayerTwoFactorRequest = z.object({
  code: playerSecondFactorCode,
});
export type DisenrolPlayerTwoFactorRequest = z.infer<typeof disenrolPlayerTwoFactorRequest>;

export const disenrolPlayerTwoFactorResult = z.object({
  status: z.literal('DISENROLLED'),
  /** How many unspent recovery codes were retired with it. */
  recoveryCodesVoided: z.number().int().nonnegative(),
  /** What a withdrawal will ask for from now on. */
  withdrawalStepUp: z.literal('PASSWORD_REQUIRED'),
});
export type DisenrolPlayerTwoFactorResult = z.infer<typeof disenrolPlayerTwoFactorResult>;

/**
 * The state of a player's own factor.
 *
 * `recoveryCodesRemaining` is a COUNT and there is no route that returns the
 * codes themselves. A player must be able to see how many they have left
 * without seeing them — that is the whole reason this number exists, and it is
 * why the count and the codes are on different endpoints rather than on one
 * endpoint with a flag.
 */
export const playerTwoFactorStatus = z.object({
  enrolled: z.boolean(),
  enrolledAt: z.iso.datetime().nullable(),
  /** Unspent and unretired. Zero is a real answer and the screen says so. */
  recoveryCodesRemaining: z.number().int().nonnegative(),
  /** True when a QR has been issued but no code from it has been proved yet. */
  enrolmentOutstanding: z.boolean(),
  /**
   * WHAT A WITHDRAWAL WILL ASK FOR, computed here rather than by the screen.
   *
   * The withdraw screen and the security screen both have to say the same
   * thing, and a screen that derives it from `enrolled` with its own `if` is a
   * screen that says the wrong thing the day the rule changes.
   */
  withdrawalStepUp: z.enum(['CODE_REQUIRED', 'PASSWORD_REQUIRED']),
});
export type PlayerTwoFactorStatus = z.infer<typeof playerTwoFactorStatus>;

/* ── The step-up that travels on a withdrawal ──────────────────────────────── */

/**
 * The proof presented with a withdrawal request.
 *
 * BOTH FIELDS ARE OPTIONAL HERE AND THE SERVER DECIDES WHICH IS REQUIRED. That
 * is not laxity: which one applies is a fact about the ACCOUNT (is a factor
 * enrolled?) read from the database inside the withdrawal's own transaction,
 * and a caller who could name their own method could name the weaker one. An
 * enrolled player who sends a password is refused and told to use their app; a
 * player with no factor who sends a code is refused and told to use their
 * password. Neither refusal is a validation error, so neither reaches this
 * schema.
 */
export const withdrawalStepUp = z.object({
  /** From the authenticator app, or one recovery code. */
  code: playerSecondFactorCode.optional(),
  /** The account password, for a player who has enrolled no factor. */
  password: z.string().min(1).max(1024).optional(),
});
export type WithdrawalStepUp = z.infer<typeof withdrawalStepUp>;

/** How a payout was authorised. Closed here and CHECKed in sql/350. */
export const PLAYER_STEP_UP_METHODS = ['TOTP', 'RECOVERY_CODE', 'PASSWORD'] as const;
export type PlayerStepUpMethod = (typeof PLAYER_STEP_UP_METHODS)[number];

/* ── The velocity limit on the password path ───────────────────────────────── */

/**
 * THE WINDOW, IN HOURS, over which a password-authorised payout is counted.
 *
 * NOT the gaming day, and that is worth stating because every other date in
 * this platform is one. A gaming-day window resets at Manila midnight, so a
 * player — or somebody holding their password — gets a fresh allowance at
 * 00:00 and can spend two windows' worth in the minutes either side of it. A
 * ROLLING 24 hours has no such seam. The gaming day is the right boundary for
 * REPORTING what happened on a day; it is the wrong boundary for a rate limit.
 */
export const PLAYER_PASSWORD_WITHDRAWAL_WINDOW_HOURS = 24;

/**
 * How many withdrawals a player with no enrolled factor may raise in that
 * window, and how much they may add up to.
 *
 * These numbers are in code rather than in `platform.setting`, unlike the
 * payout SLA and the payout-ratio hold beside them. The difference is what
 * happens when the value is ABSENT: an unset SLA means the queue has no clock
 * and the request is refused, and an unset ratio hold means no ratio hold. An
 * unset velocity limit would mean NO LIMIT — a security control that silently
 * switches itself off on any brand nobody configured, which is the shape of a
 * control that is not one. A default in code always applies.
 *
 * The figures themselves: three requests and fifty thousand pesos a day is far
 * above what an ordinary player does and far below what somebody emptying an
 * account does. A player who wants neither limit enrols an authenticator, which
 * is the trade this whole feature offers and the screen says so in those words.
 */
export const PLAYER_PASSWORD_WITHDRAWAL_MAX_REQUESTS = 3;

/** In CENTAVOS, as a bigint. Money is never a float and never a JSON number. */
export const PLAYER_PASSWORD_WITHDRAWAL_MAX_CENTAVOS = 5_000_000n;

/**
 * Re-exported so a screen quoting the password floor at the step-up reads it
 * from the same place the sign-up form does.
 */
export const PLAYER_STEP_UP_PASSWORD_MINIMUM = PLAYER_PASSWORD_MINIMUM;

/* ── Being issued a fresh set of recovery codes ───────────────────────────── */

/**
 * REPLACING THE CODES WITHOUT REMOVING THE FACTOR.
 *
 * The security screen used to tell a player with none left to "switch the
 * authenticator off with a code from the app and set it up again to be issued a
 * fresh set". That advice works, and it is the wrong advice: it walks somebody
 * through DISARMING their own second factor, opens a window in which the
 * account has none, and hands them a new secret to re-enrol — all to obtain
 * something the database was built to give them directly.
 * `identity.player_recovery_code.voided_at` exists precisely so a batch can be
 * retired without being spent and without being deleted.
 *
 * THE PROOF IS THE SAME ONE DISENROLLING ASKS FOR, and deliberately NOT the
 * password. This route hands the caller ten fresh bypasses of the withdrawal
 * step-up; accepting the credential most likely to have been stolen as the way
 * to mint them would make the step-up removable by exactly the attacker it
 * defends against. A recovery code is accepted as well as a live code, and the
 * one presented is spent doing it — which is correct, because every unspent
 * code in the batch is being retired in the same breath anyway.
 */
export const reissuePlayerRecoveryCodesRequest = z.object({
  code: playerSecondFactorCode,
});
export type ReissuePlayerRecoveryCodesRequest = z.infer<
  typeof reissuePlayerRecoveryCodesRequest
>;

/**
 * What reissuing returns — the codes, ONCE, and what it retired to make room.
 *
 * `recoveryCodesVoided` is reported rather than assumed: a player who had three
 * left and now has ten must be able to see that the three no longer work, and a
 * screen that only said "here are your new codes" would leave somebody holding
 * a printout they believe is still good.
 */
export const reissuePlayerRecoveryCodesResult = z.object({
  status: z.literal('REISSUED'),
  /** Shown once. The database holds only their digests. */
  recoveryCodes: z.array(z.string()).min(1),
  /** How many unspent codes from the previous batch were retired. */
  recoveryCodesVoided: z.number().int().nonnegative(),
  /** Unchanged by this route, and stated so the screen never has to infer it. */
  withdrawalStepUp: z.literal('CODE_REQUIRED'),
});
export type ReissuePlayerRecoveryCodesResult = z.infer<
  typeof reissuePlayerRecoveryCodesResult
>;
